import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from './auth';

export type NoteStatus = 'idle' | 'saving' | 'saved' | 'pending' | 'error';

interface RemoteNote {
  cardId: string;
  deckSlug: string;
  body: string;
  clientUpdatedAt: string;
}

interface PendingEdit {
  deckSlug: string;
  cardId: string;
  body: string;
  clientUpdatedAt: string;
}

/**
 * Personal card notes.
 *
 * The server holds the only copy, so notes follow the account rather than the
 * browser. An edit that cannot reach it (offline) is kept in memory and sent
 * on the next connection; it is never written to the device, where the next
 * account to sign in could read it.
 */
export const useNotesStore = defineStore('notes', () => {
  const bodies = ref<Record<string, string>>({});
  const status = ref<NoteStatus>('idle');
  /** Edits the server has not acknowledged yet, by `${deckSlug}:${cardId}`. */
  const pending = new Map<string, PendingEdit>();
  /** The account whose notes `bodies` holds; null until they are fetched. */
  let loadedFor: string | null = null;
  let loading: Promise<void> | null = null;

  const auth = useAuthStore();
  const key = (deckSlug: string, cardId: string): string => `${deckSlug}:${cardId}`;

  // A different reader: drop everything of the previous one's, unsent edits
  // included — they were written under the other account.
  watch(
    () => auth.user?.id ?? null,
    () => {
      bodies.value = {};
      status.value = 'idle';
      pending.clear();
      loadedFor = null;
      loading = null;
    },
  );

  /** Fetches every note for the signed-in account, once per account. */
  function fetchAll(): Promise<void> {
    const userId = auth.user?.id ?? null;
    if (!userId || !auth.entitlements.notes || loadedFor === userId) return Promise.resolve();

    loading ??= (async () => {
      try {
        const remote = await api<{ notes: RemoteNote[] }>('/notes');
        if (auth.user?.id !== userId) return;

        const next: Record<string, string> = {};
        for (const note of remote.notes) next[key(note.deckSlug, note.cardId)] = note.body;
        // An edit still waiting to go up is newer than what the server has.
        for (const [k, edit] of pending) next[k] = edit.body;

        bodies.value = next;
        loadedFor = userId;
      } finally {
        loading = null;
      }
    })();
    return loading;
  }

  async function load(deckSlug: string, cardId: string): Promise<string> {
    try {
      await fetchAll();
    } catch {
      // Offline: show whatever this session already has.
    }
    return bodies.value[key(deckSlug, cardId)] ?? '';
  }

  async function push(edit: PendingEdit): Promise<void> {
    await api(`/notes/${encodeURIComponent(edit.cardId)}`, {
      method: 'PUT',
      body: { deckSlug: edit.deckSlug, body: edit.body, clientUpdatedAt: edit.clientUpdatedAt },
    });
    const k = key(edit.deckSlug, edit.cardId);
    // Only clear it if no newer edit replaced it while this one was in flight.
    if (pending.get(k) === edit) pending.delete(k);
  }

  /**
   * Persists an edit. Called from the debounced autosave, and directly on
   * blur, tab-hide and navigation so a pending edit is never lost.
   */
  async function save(deckSlug: string, cardId: string, body: string): Promise<void> {
    if (!auth.entitlements.notes) return;

    const k = key(deckSlug, cardId);
    bodies.value = { ...bodies.value, [k]: body };
    status.value = 'saving';

    const edit: PendingEdit = { deckSlug, cardId, body, clientUpdatedAt: new Date().toISOString() };
    pending.set(k, edit);

    try {
      await push(edit);
      status.value = pending.size ? 'pending' : 'saved';
    } catch (cause) {
      // Offline is expected: the edit is held and goes up on reconnect.
      status.value = cause instanceof ApiError && cause.isOffline ? 'pending' : 'error';
    }
  }

  async function remove(deckSlug: string, cardId: string): Promise<void> {
    const k = key(deckSlug, cardId);
    pending.delete(k);
    const next = { ...bodies.value };
    delete next[k];
    bodies.value = next;

    if (!auth.entitlements.notes) return;
    try {
      await api(`/notes/${encodeURIComponent(cardId)}`, {
        method: 'DELETE',
        query: { deck: deckSlug },
      });
    } catch {
      // Best effort.
    }
  }

  /**
   * Sends edits held while offline, then refreshes from the server. Called
   * after signing in and on reconnect.
   */
  async function sync(): Promise<void> {
    if (!auth.entitlements.notes) return;

    try {
      for (const edit of [...pending.values()]) await push(edit);
      if (status.value === 'pending' && !pending.size) status.value = 'saved';

      loadedFor = null;
      await fetchAll();
    } catch {
      // Still offline. The edits stay queued for the next attempt.
    }
  }

  const bodyFor = (deckSlug: string, cardId: string): string =>
    bodies.value[key(deckSlug, cardId)] ?? '';

  return { bodies, status, load, save, remove, sync, bodyFor };
});
